import {KeyboardArrowDown} from '@mui/icons-material';
import {
    Box,
    FormControl,
    MenuItem,
    MenuItemProps,
    Select,
    SelectChangeEvent,
    SxProps,
    Theme,
    Typography,
} from '@mui/material';
import {FC, FocusEvent} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import {SelectValue} from 'src/types';
import {ColoredStatus} from '../ColoredStatus';
import {ColorField} from '../ColorField';
import {CustomFormLabel} from '../CustomFormLabel';
import {FormHelper} from '../FormHelper';
import {containerSx, denseContainerSx, descriptionSx, placeholderSx, placeholderWrapperSx,} from './AMPFormSelect.sx';

interface SelectInputProps {
    label?: string;
    input: string;
    disabled?: boolean;
    value: string | number;
    onBlur?: (e: FocusEvent<any>) => void;
    onChange?: (e: SelectChangeEvent<any>) => void;
    onClick?: (value: string | number) => void;
    hasError?: boolean | string;
    helperText?: string;
    className?: string;
    required?: boolean;
    placeholder?: string;
    description?: string;
    sx?: SxProps<Theme>;
    select?: boolean;
    data: SelectValue[];
    defaultValue?: boolean;
    footerContent?: React.ReactNode;
    /** Drops the form margin. For a toolbar, where the control is level with its neighbours. */
    dense?: boolean;
}

export const AMPFormSelect: FC<SelectInputProps> = ({
                                                        input,
                                                        hasError,
                                                        helperText,
                                                        data,
                                                        label,
                                                        onChange,
                                                        onClick,
                                                        description,
                                                        disabled,
                                                        value,
                                                        footerContent,
                                                        dense,
                                                        ...rest
                                                    }) => {
    const intl = useIntl();

    const handleClick =
        (value: string | number): MenuItemProps['onClick'] =>
            (_e) => {
                onClick && onClick(value);
            };

    const inputIsStatus = input === 'status';
    const inputIsRole = input === 'role';

    return (
        <FormControl
            fullWidth
            variant="outlined"
            error={!!hasError}
            // `normal` is 16px above and 8px below. In a toolbar the eight below is what lifts the
            // control off the line its neighbours sit on — it is half a gap of dead space that the
            // row centres around, so the select reads as sitting higher than everything else.
            margin={dense ? 'none' : 'normal'}
            sx={dense ? denseContainerSx : containerSx}
        >
            {label && (
                <CustomFormLabel
                    label={intl.formatMessage({id: label})}
                    required={rest.required}
                />
            )}
            <Box sx={placeholderWrapperSx}>
                {!value && (
                    <Typography
                        sx={placeholderSx}
                        variant="h5"
                    >
                        <FormattedMessage id="select"/>
                    </Typography>
                )}
                <Select
                    labelId={input}
                    id={input}
                    name={input}
                    IconComponent={KeyboardArrowDown}
                    onChange={onChange}
                    defaultValue={data.length ? data[0].value : ''}
                    value={data.length ? value : ''}
                    disabled={!data.length || disabled}
                    sx={rest.sx}
                >
                    {data?.length &&
                        data?.map((item: SelectValue) => {
                            return (
                                <MenuItem
                                    key={item.id}
                                    value={item.value}
                                    onClick={handleClick(item.value)}
                                >
                                    {/* The chip is for a real status or role; an "all" entry is
                                        neither. It also renders its label through FormattedMessage
                                        as an id, and an "all" entry's name is already translated
                                        text - which is what raised MISSING_TRANSLATION for
                                        "All statuses" and would for every other one. */}
                                    {inputIsStatus && item.value !== '' && <ColoredStatus label={item.name}/>}
                                    {inputIsRole && item.value !== '' && <ColorField label={item.name}/>}
                                    {(item.value === '' || !(inputIsStatus || inputIsRole)) && item.name}
                                </MenuItem>
                            );
                        })}
                    {footerContent}
                </Select>
            </Box>
            {description && (
                <Typography sx={descriptionSx}>{intl.formatMessage({id: description})}</Typography>
            )}
            {helperText && <FormHelper helperText={helperText}/>}
        </FormControl>
    );
};
